import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useMesero } from "@/modules/mesero/MeseroProvider";
import { MeseroFrame } from "@/modules/mesero/components/MeseroFrame";
import { MeseroBottomNav } from "@/modules/mesero/components/MeseroBottomNav";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { Reservation, Table } from "@/lib/pos-types";
import { cn } from "@/lib/utils";
import { Calendar, Users, Utensils, Table as TableIcon, Loader2, AlertTriangle } from "lucide-react";

export default function ReservasPage() {
  const navigate = useNavigate();
  const {
    reservations,
    tables,
    zones,
    getUnreadAlertsCount,
    assignTableToReservation,
  } = useMesero();

  const unreadCount = getUnreadAlertsCount();

  const [assignCandidate, setAssignCandidate] = useState<Reservation | null>(null);
  const [loadingResId, setLoadingResId] = useState<string | null>(null);
  const [assigningTableId, setAssigningTableId] = useState<string | null>(null);
  const [confirmOccupiedTable, setConfirmOccupiedTable] = useState<Table | null>(null);

  // Active / scheduled reservations WITHOUT a table assigned
  const tablelessReservations = useMemo(() => {
    return (reservations ?? [])
      .filter((r) => r.status === "scheduled" && !r.tableId)
      .sort((a, b) => new Date(a.reservedAt).getTime() - new Date(b.reservedAt).getTime());
  }, [reservations]);

  const assignableTablesByZone = useMemo(() => {
    // Incluir TODAS las mesas del restaurante para que el mesero pueda seleccionar cualquiera
    const assignable = tables;
    const zoneMap = new Map<string, Table[]>();
    for (const t of assignable) {
      const zName = t.zone?.trim() || "Sin Zona";
      if (!zoneMap.has(zName)) {
        zoneMap.set(zName, []);
      }
      zoneMap.get(zName)!.push(t);
    }

    const orderedZoneNames: string[] = [];
    if (zones && zones.length > 0) {
      zones.forEach((z) => {
        if (z && zoneMap.has(z)) orderedZoneNames.push(z);
      });
    }
    zoneMap.forEach((_, z) => {
      if (!orderedZoneNames.includes(z)) orderedZoneNames.push(z);
    });

    return orderedZoneNames.map((zoneName) => ({
      zone: zoneName,
      tables: zoneMap.get(zoneName)!.sort((a, b) => a.number - b.number),
    }));
  }, [tables, zones]);

  const handleAssignTable = async (table: Table) => {
    if (!assignCandidate) return;
    const resId = assignCandidate.id;
    setLoadingResId(resId);
    setAssigningTableId(table.id);
    const isOccupied = table.status === "occupied" || table.status === "pending-payment" || Boolean(table.currentOrderId);
    const toastMessage = isOccupied
      ? `Sumando reserva a Mesa ${table.number}...`
      : `Asignando Mesa ${table.number} a ${assignCandidate.reserverName}...`;
    const toastId = toast.loading(toastMessage);
    try {
      const assignedTable = await assignTableToReservation(resId, table.id);
      toast.dismiss(toastId);
      setAssignCandidate(null);
      if (assignedTable) {
        if (isOccupied) {
          toast.success(`Reserva sumada exitosamente a la Mesa ${table.number}`);
        }
        navigate(`/mesero/comanda/${assignedTable.id}`);
      }
    } catch {
      toast.dismiss(toastId);
    } finally {
      setLoadingResId(null);
      setAssigningTableId(null);
    }
  };

  const onSelectTable = (table: Table) => {
    const isBusy = table.status === "occupied" || table.status === "pending-payment" || table.status === "blocked" || Boolean(table.currentOrderId);
    if (isBusy) {
      setConfirmOccupiedTable(table);
    } else {
      void handleAssignTable(table);
    }
  };

  const handleConfirmOccupiedMerge = () => {
    if (confirmOccupiedTable) {
      const targetTable = confirmOccupiedTable;
      setConfirmOccupiedTable(null);
      void handleAssignTable(targetTable);
    }
  };

  return (
    <MeseroFrame>
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Top Header */}
        <div className="px-5 pt-3 pb-3 flex items-center justify-between shrink-0 border-b border-white/5">
          <div>
            <div className="text-[22px] font-semibold text-[#f0f0f0] leading-tight flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#00e5a0]" />
              <span>Reservas sin mesa</span>
            </div>
            <div className="text-[12px] text-[#9a9aa8] mt-0.5">
              {tablelessReservations.length} reservas pendientes de asignar mesa
            </div>
          </div>
        </div>

        {/* Reservation Cards List */}
        <div className="flex-1 overflow-y-auto px-5 pt-4 pb-5 space-y-3">
          {tablelessReservations.length === 0 ? (
            <div className="text-center py-16 text-[#5a5a6a] text-sm">
              No hay reservas sin mesa en este momento.
            </div>
          ) : (
            tablelessReservations.map((r) => {
              const formattedTime = formatReservationTime(r.reservedAt);
              const itemsCount = r.items?.length ?? 0;
              const isPending = loadingResId === r.id;

              return (
                <div
                  key={r.id}
                  className="rounded-2xl p-4 bg-[#1e1e23] border border-white/10 flex flex-col gap-3 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-base font-semibold text-[#f0f0f0] flex items-center gap-2">
                        <span>{r.reserverName}</span>
                      </div>
                      {r.reserverPhone && (
                        <div className="text-xs text-[#9a9aa8] mt-0.5">
                          Tel: {r.reserverPhone}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-[#00e5a0]">
                        {formattedTime}
                      </div>
                      <div className="flex items-center justify-end gap-1 text-xs text-[#9a9aa8] mt-0.5">
                        <Users className="h-3.5 w-3.5" />
                        <span>{r.partySize} pers.</span>
                      </div>
                    </div>
                  </div>

                  {/* Pre-order Info */}
                  <div className="flex items-center justify-between pt-1 border-t border-white/5 text-xs">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[rgba(245,166,35,0.14)] text-[#f5a623] font-medium border border-[rgba(245,166,35,0.30)]">
                      <span>Sin mesa asignada</span>
                    </div>

                    {itemsCount > 0 && (
                      <div className="flex items-center gap-1 text-[#9a9aa8]">
                        <Utensils className="h-3.5 w-3.5 text-[#00e5a0]" />
                        <span>{itemsCount} productos pre-guardados</span>
                      </div>
                    )}
                  </div>

                  {r.notes && (
                    <div className="text-xs italic text-[#9a9aa8] bg-[#17171a] p-2 rounded-lg border border-white/5">
                      "{r.notes}"
                    </div>
                  )}

                  {/* Action Button */}
                  <div className="pt-1">
                    <button
                      type="button"
                      disabled={isPending}
                      onClick={() => setAssignCandidate(r)}
                      className="w-full py-2.5 rounded-xl bg-[#4d9fff] text-black font-semibold text-xs flex items-center justify-center gap-1.5 hover:bg-[#3b82f6] transition-colors disabled:opacity-50"
                    >
                      {isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin text-black" />
                          <span>Asignando...</span>
                        </>
                      ) : (
                        <>
                          <span>Asignar Mesa</span>
                          <TableIcon className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Bottom Navigation */}
        <MeseroBottomNav alertsCount={unreadCount} />
      </div>

      {/* Drawer for Assigning Table */}
      <Drawer
        open={Boolean(assignCandidate)}
        onOpenChange={(open) => !open && !assigningTableId && setAssignCandidate(null)}
      >
        <DrawerContent className="bg-[#1e1e23] border-t border-white/10 text-[#f0f0f0]">
          <div className="mx-auto mt-4 h-1 w-10 rounded-full bg-white/15" />
          <DrawerHeader className="text-left px-5">
            <DrawerTitle className="text-[#f0f0f0] text-base font-semibold">
              Asignar Mesa para {assignCandidate?.reserverName}
            </DrawerTitle>
            <DrawerDescription className="text-[#9a9aa8] text-xs">
              Reserva para {assignCandidate?.partySize} personas. Selecciona una mesa disponible u ocupada.
            </DrawerDescription>
          </DrawerHeader>

          <div className="px-5 pb-4 max-h-[60vh] overflow-y-auto space-y-4">
            {assignableTablesByZone.length === 0 ? (
              <div className="text-center py-6 text-sm text-[#9a9aa8]">
                No hay mesas asignables en este momento.
              </div>
            ) : (
              assignableTablesByZone.map(({ zone, tables: zoneTables }) => (
                <div key={zone} className="space-y-2">
                  <div className="flex items-center gap-2 pt-1 border-b border-white/10 pb-1.5">
                    <span className="h-2 w-2 rounded-full bg-[#00e5a0]" />
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#00e5a0]">
                      {zone}
                    </h4>
                    <span className="text-[10px] text-[#9a9aa8] bg-white/5 px-2 py-0.5 rounded-full font-mono">
                      {zoneTables.length} {zoneTables.length === 1 ? "mesa" : "mesas"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    {zoneTables.map((t) => {
                      const isThisAssigning = assigningTableId === t.id;
                      const isAnyAssigning = Boolean(assigningTableId);
                      const isOccupied = t.status === "occupied" || t.status === "pending-payment" || Boolean(t.currentOrderId);
                      const isBlocked = t.status === "blocked" && !isOccupied;

                      return (
                        <button
                          key={t.id}
                          type="button"
                          disabled={isAnyAssigning}
                          onClick={() => onSelectTable(t)}
                          className={cn(
                            "p-3 rounded-xl border text-left flex flex-col gap-1 transition-all",
                            isThisAssigning
                              ? "bg-[rgba(0,229,160,0.15)] border-[#00e5a0]"
                              : isOccupied
                              ? "bg-[rgba(245,166,35,0.08)] border-[rgba(245,166,35,0.30)] hover:border-[#f5a623]"
                              : isBlocked
                              ? "bg-red-500/10 border-red-500/30 hover:border-red-400"
                              : "bg-[#26262d] border-white/10 hover:border-[#00e5a0] active:scale-95",
                            isAnyAssigning && !isThisAssigning && "opacity-40"
                          )}
                        >
                          <div className="text-sm font-semibold text-[#f0f0f0] flex items-center justify-between">
                            <span>Mesa {t.number}</span>
                            {isThisAssigning ? (
                              <Loader2 className="h-4 w-4 animate-spin text-[#00e5a0]" />
                            ) : isOccupied ? (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#f5a623]/20 text-[#f5a623] font-medium">
                                {t.status === "pending-payment" ? "Cuenta" : "Ocupada"}
                              </span>
                            ) : isBlocked ? (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-medium">
                                Bloqueada
                              </span>
                            ) : (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00e5a0]/20 text-[#00e5a0] font-medium">
                                Libre
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-[#9a9aa8]">
                            {isThisAssigning
                              ? "Asignando..."
                              : isOccupied
                              ? `Ocupada ${t.waiter ? `· ${t.waiter}` : ''}`
                              : isBlocked
                              ? `Bloqueada ${t.blockedReason ? `· ${t.blockedReason}` : ''}`
                              : `Cap: ${t.capacity} pers`}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          <DrawerFooter className="px-5 pb-8">
            <DrawerClose asChild>
              <button
                type="button"
                className="w-full py-3 rounded-xl border border-white/10 bg-[#26262d] text-[#f0f0f0] text-sm font-medium"
              >
                Cancelar
              </button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Confirmation Modal for Occupied Table Assignment */}
      <AlertDialog
        open={Boolean(confirmOccupiedTable)}
        onOpenChange={(open) => !open && setConfirmOccupiedTable(null)}
      >
        <AlertDialogContent className="bg-[#1e1e23] border border-white/10 text-[#f0f0f0] max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-semibold text-[#f0f0f0] flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-[#f5a623]" />
              <span>¿Sumar reserva a Mesa {confirmOccupiedTable?.number}?</span>
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-[#9a9aa8] space-y-2.5 mt-2">
              <span className="block text-[#d0d0d8]">
                La <strong>Mesa {confirmOccupiedTable?.number}</strong> ya tiene una comanda activa en curso.
              </span>
              <span className="block bg-[#17171a] p-3 rounded-xl border border-white/5 space-y-1">
                <span className="block font-medium text-[#f0f0f0]">
                  Reserva: {assignCandidate?.reserverName}
                </span>
                <span className="block text-[#9a9aa8]">
                  • {assignCandidate?.partySize} personas
                  {assignCandidate?.items?.length ? ` · ${assignCandidate.items.length} productos pre-guardados` : ''}
                </span>
              </span>
              <span className="block text-[#9a9aa8]">
                Al confirmar, el contenido y los comensales de la reserva se **sumarán automáticamente** a la comanda activa de la Mesa {confirmOccupiedTable?.number}.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 mt-3">
            <AlertDialogCancel className="bg-[#26262d] border-white/10 text-[#f0f0f0] hover:bg-[#32323a] text-xs py-2.5">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmOccupiedMerge}
              className="bg-[#f5a623] hover:bg-[#d98e19] text-black font-semibold text-xs py-2.5"
            >
              Confirmar y Sumar a Comanda
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MeseroFrame>
  );
}

function formatReservationTime(reservedAt: Date | string): string {
  try {
    const d = typeof reservedAt === "string" ? new Date(reservedAt) : reservedAt;
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm} hrs`;
  } catch {
    return String(reservedAt);
  }
}
